import { useEffect, useState } from 'react';
import type { VitalSigns } from '@/types/db';
import type { TimelineEventWithExtras } from '../lib/timelineQueries';
import { formatLongDate } from '../lib/formatDate';
import { formatDoctorName } from '../lib/formatDoctor';
import { FlagList, ResolvedFlagsGroup } from './flag-chip';
import { PhotoLightbox } from './photo-lightbox';
import { signedPrescriptionUrl } from '../lib/upload';
import { PdfPreview } from '@/components/pdf-preview';

function getBMILabel(bmi: number): { text: string; colorClass: string } {
  if (bmi < 18.5) return { text: 'Underweight', colorClass: 'text-flag-info' };
  if (bmi < 25) return { text: 'Normal', colorClass: 'text-[#2E7D32]' };
  if (bmi < 30) return { text: 'Overweight', colorClass: 'text-flag-warn' };
  return { text: 'Obese', colorClass: 'text-flag-block' };
}

interface VitalRow {
  label: string;
  value: string;
  unit: string;
}

function VitalsSection({ vitals }: { vitals: VitalSigns | undefined }) {
  if (!vitals) return null;
  const rows: VitalRow[] = [];
  if (vitals.heart_rate != null)
    rows.push({ label: 'Heart rate', value: String(vitals.heart_rate), unit: 'bpm' });
  if (vitals.systolic_bp != null && vitals.diastolic_bp != null)
    rows.push({ label: 'Blood pressure', value: `${vitals.systolic_bp}/${vitals.diastolic_bp}`, unit: 'mmHg' });
  if (vitals.blood_sugar != null)
    rows.push({ label: 'Blood sugar', value: String(vitals.blood_sugar), unit: 'mg/dL' });
  if (vitals.weight_kg != null)
    rows.push({ label: 'Weight', value: String(vitals.weight_kg), unit: 'kg' });
  if (vitals.height_cm != null)
    rows.push({ label: 'Height', value: String(vitals.height_cm), unit: 'cm' });
  if (vitals.bmi != null) {
    const status = getBMILabel(vitals.bmi);
    rows.push({ label: 'BMI', value: String(vitals.bmi), unit: status.text });
  }
  if (rows.length === 0) return null;
  return (
    <section>
      <h3 className="text-xs font-medium text-ink-muted uppercase tracking-wide mb-2">
        Vitals
      </h3>
      <div className="grid grid-cols-2 gap-2">
        {rows.map((r) => (
          <div key={r.label} className="rounded-lg border border-border p-3">
            <p className="text-xs text-ink-muted">{r.label}</p>
            <p className="font-semibold text-ink">
              {r.value}{' '}
              <span className="text-sm font-normal text-ink-muted">{r.unit}</span>
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

interface Props {
  event: TimelineEventWithExtras;
  onClose: () => void;
  onEdit?: () => void;
}

export function EventDetail({ event, onClose, onEdit }: Props) {
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [lightbox, setLightbox] = useState<{ url: string; alt: string } | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      event.attachments.map(async (a) => {
        try {
          const url = await signedPrescriptionUrl(a.storage_path);
          return [a.id, url] as const;
        } catch (err) {
          console.error('[event-detail] signed url failed', err);
          return null;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      const next: Record<string, string> = {};
      for (const entry of entries) {
        if (entry) next[entry[0]] = entry[1];
      }
      setPhotoUrls(next);
    });
    return () => {
      cancelled = true;
    };
  }, [event.attachments]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-40 bg-ink/50 flex items-end sm:items-center sm:justify-center"
      onClick={onClose}
    >
      <div
        className="bg-surface w-full sm:max-w-lg sm:rounded-card rounded-t-card max-h-[92vh] overflow-y-auto safe-bottom"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 bg-surface border-b border-border px-4 py-3 flex items-center gap-2">
          <h2 className="font-bold flex-1 truncate">
            {event.diagnosis ||
              event.title ||
              (event.event_type === 'prescription'
                ? 'Prescription'
                : 'Record')}
          </h2>
          <div className="flex items-center shrink-0 gap-1">
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                className="inline-flex items-center gap-1.5 text-sm font-semibold min-h-tap px-3 rounded-full bg-canvas border border-border text-ink hover:border-border-strong active:bg-border/50"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-3.5 w-3.5"
                  aria-hidden="true"
                >
                  <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 3 21l.5-4.5z" />
                </svg>
                Edit
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-ink-muted min-w-tap min-h-tap -mr-2 flex items-center justify-center"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </header>

        <div className="p-4 space-y-4">
          <div className="text-sm">
            <p className="text-ink-muted">{formatLongDate(event.event_date)}</p>
            {event.doctor_name && (
              <p className="mt-1">
                <span className="text-ink-muted">Doctor:</span>{' '}
                <span className="font-medium">
                  {formatDoctorName(event.doctor_name)}
                </span>
              </p>
            )}
            {event.clinic_name && (
              <p className="mt-1">
                <span className="text-ink-muted">Clinic:</span>{' '}
                <span className="font-medium">{event.clinic_name}</span>
              </p>
            )}
          </div>

          {event.ai_flags.length > 0 && (() => {
            const active = event.ai_flags.filter((f) => !f.resolved_at);
            const resolved = event.ai_flags.filter((f) => !!f.resolved_at);
            return (
              <div>
                {active.length > 0 && <FlagList flags={active} />}
                <ResolvedFlagsGroup flags={resolved} />
              </div>
            );
          })()}

          <VitalsSection vitals={event.vital_signs} />

          {event.medications.length > 0 && (
            <section>
              <h3 className="text-xs font-medium text-ink-muted uppercase tracking-wide mb-2">
                Medications
              </h3>
              <ul className="space-y-2">
                {event.medications.map((med) => (
                  <li
                    key={med.id}
                    className="rounded-lg border border-border p-3"
                  >
                    <p className="font-medium">{med.drug_name ?? 'Unnamed'}</p>
                    <p className="text-sm text-ink-muted">
                      {[med.dosage, med.frequency].filter(Boolean).join(' · ') ||
                        'Dosage not recorded'}
                      {med.duration_days
                        ? ` · ${med.duration_days} day${med.duration_days === 1 ? '' : 's'}`
                        : ''}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {event.notes && (
            <section>
              <h3 className="text-xs font-medium text-ink-muted uppercase tracking-wide mb-1">
                Notes
              </h3>
              <p className="text-sm whitespace-pre-wrap">{event.notes}</p>
            </section>
          )}

          {event.attachments.length > 0 && (
            <section>
              <h3 className="text-xs font-medium text-ink-muted uppercase tracking-wide mb-2">
                {event.attachments.some((a) => a.file_type === 'application/pdf') ? 'Attachment' : 'Photo'}
              </h3>
              <div className="space-y-3">
                {event.attachments.map((a) => {
                  const url = photoUrls[a.id];
                  if (!url) {
                    return (
                      <div
                        key={a.id}
                        className="h-40 bg-canvas rounded-lg animate-pulse"
                      />
                    );
                  }
                  if (a.file_type === 'application/pdf') {
                    return (
                      <PdfPreview
                        key={a.id}
                        url={url}
                        fileName={a.file_name}
                        variant="compact"
                      />
                    );
                  }
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() =>
                        setLightbox({ url, alt: a.file_name ?? 'Attachment' })
                      }
                      aria-label="Tap to zoom photo"
                      className="block w-full"
                    >
                      <img
                        src={url}
                        alt={a.file_name ?? 'Attachment'}
                        className="w-full h-auto rounded-lg bg-canvas"
                      />
                    </button>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>

      {lightbox && (
        <PhotoLightbox
          src={lightbox.url}
          alt={lightbox.alt}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
